/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from 'react';
import logo from '../../assets/logo.svg';
import { keyframes } from '@emotion/react';
import { Lock } from 'lucide-react';

// Define the animation
const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

// Add pulsing animation for the CTA buttons
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

function ContentBriefComponent() {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem('contentBriefToken');
    if (storedToken === 'spicypeach') {
      setIsAuthenticated(true);
    }
  }, []);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'spicypeach') {
      localStorage.setItem('contentBriefToken', 'spicypeach');
      setIsAuthenticated(true);
      setPassword('');
    } else {
      alert('Incorrect password');
      setPassword('');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#7B1C14] py-12 px-4 sm:px-6 lg:px-8 font-sans">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <Lock className="mx-auto h-12 w-12 text-[#F7E8CC]" />
            <h1 className="mt-6 text-3xl font-bold text-[#F7E8CC]">
              Protected Content Brief
            </h1>
          </div>
          <form onSubmit={handlePasswordSubmit} className="mt-8 space-y-6">
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none rounded relative block w-full px-3 py-2 border border-[#F7E8CC]/20 bg-[#6E1912] placeholder-[#F7E8CC]/50 text-[#F7E8CC] focus:outline-none focus:ring-2 focus:ring-[#F7E8CC]/50 focus:border-transparent"
                  placeholder="Enter password"
                />
              </div>
            </div>
            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-[#7B1C14] bg-[#F7E8CC] hover:bg-[#F7E8CC]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F7E8CC]/50"
              >
                Access Content Brief
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#7B1C14] py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="text-center mb-8">
          <img 
            src={logo} 
            alt="Spicy Cubes" 
            className="h-16 sm:h-20 md:h-24 mx-auto"
          />
        </div>
        
        <div className="bg-[#6E1912] rounded-lg shadow-lg p-8">
          <div className="relative pb-6 mb-6 border-b border-[#F7E8CC]/10">
            <h1 className="text-3xl font-bold text-[#F7E8CC] text-center">🔥 Spicy Cubes Content Brief 🔥</h1>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-48 h-[2px] bg-gradient-to-r from-transparent via-[#F7E8CC]/10 to-transparent" />
          </div>

          {/* About the Brand */}
          <div className="mt-6 bg-[#5E150F] p-4 rounded-md">
            <h3 className="font-bold text-lg mb-3 text-[#FFD700]">About the Brand</h3>
            <p className="text-[#F7E8CC]/90">
              Sweet & spicy aphrodisiac for two that boosts mood & <em>sensual</em> experience.
            </p>
            <p className="mt-2 text-[#F7E8CC]/90">
              <span className="font-semibold">Website:</span> <a href="https://spicycubes.co" className="text-[#FF851B] hover:underline" target="_blank" rel="noopener noreferrer">Spicycubes.co</a>
            </p>
          </div>

          {/* Objective & Target Audience */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#5E150F] p-4 rounded-md">
              <h3 className="font-bold text-lg mb-3 text-[#FFD700]">Objective</h3>
              <ul className="list-disc pl-5 space-y-1 text-[#F7E8CC]/90">
                <li>VIRALITY</li>
                <li>Produce high quality content around the product that is engaging & relatable to viewers</li>
              </ul>
            </div>

            <div className="bg-[#5E150F] p-4 rounded-md">
              <h3 className="font-bold text-lg mb-3 text-[#FFD700]">Target Audience</h3>
              <ul className="list-disc pl-5 space-y-1 text-[#F7E8CC]/90">
                <li>Women/couples looking to spice up dating/sex life</li>
                <li>Individuals/women looking to boost libido confidence</li>
              </ul>
            </div>
          </div>

          {/* Types of Content */}
          <div className="mt-6 bg-[#5E150F] p-4 rounded-md">
            <h3 className="font-bold text-lg mb-3 text-[#FFD700]">Type of Content</h3>
            <ul className="list-disc pl-5 space-y-2 text-[#F7E8CC]/90">
              <li>Viral story telling <span className="text-[#FFD700] italic">(most popular)</span></li>
            </ul>
          </div>

          {/* Important Notice */}
          <div className="mt-6 bg-[#5E150F]/30 p-4 rounded-md border border-[#FFD700]/30">
            <p className="text-[#F7E8CC] font-medium">
              You <span className="text-[#FFD700] font-bold">MUST</span> post once a day, make sure your accounts are warmed up before posting. Check out the account setup/warming guides if you haven't already:
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <a href="/tiktok-account-setup" 
                 className="px-3 py-1 bg-[#FF4136] text-[#F7E8CC] rounded-md text-sm hover:bg-[#FF2718] transition-colors">
                TikTok Setup
              </a>
              <a href="/instagram-account-setup" 
                 className="px-3 py-1 bg-[#FF851B] text-[#F7E8CC] rounded-md text-sm hover:bg-[#FF6700] transition-colors">
                Instagram Setup
              </a>
              <a href="/youtube-shorts-account-setup" 
                 className="px-3 py-1 bg-[#FFB700] text-[#F7E8CC] rounded-md text-sm hover:bg-[#FFA200] transition-colors">
                YouTube Shorts Setup
              </a>
            </div>
            <p className="mt-3 text-[#F7E8CC] font-semibold">
              Your first 5 videos should just be the scripts linked <a href="https://docs.google.com/document/d/1BUZY-DdhM1PzTQZNFHF0ejnoRNvAel3CNnw9m6RAazE/edit?tab=t.0" 
                className="text-[#FFD700] underline" target="_blank" rel="noopener noreferrer">here</a>
            </p>
          </div>

          {/* Hooks Section */}
          <div className="mt-8 bg-[#6E1912] rounded-lg shadow-inner p-6">
            <div className="bg-[#5E150F] rounded-lg p-4 mb-6 border-2 border-[#FFD700]/50 shadow-lg">
              <h2 className="text-2xl font-bold text-center text-[#FFD700]">
                🪝 HOOKS 🪝
              </h2>
              <p className="text-center font-semibold mt-2 text-[#F7E8CC]">
              Combine visual and text hooks to create a hook that stops the scroll within the first 
              <span className="text-[#FFD700]"> 3 seconds</span>.
              </p>
            </div>

            {/* Visual Hooks */}
            <div className="bg-[#5E150F]/70 p-4 rounded-md mb-6">
              <h3 className="font-bold text-lg mb-3 text-[#FFD700]">Visual Hooks</h3>
              <p className="text-[#F7E8CC]/90 italic mb-3">
                Compelling visual elements that capture the audience's attention when story telling.
              </p>
              <p className="text-[#F7E8CC]/90 font-medium mb-2">Examples:</p>
              <ul className="list-disc pl-5 space-y-1 text-[#F7E8CC]/90">
                <li>The shaky phone (setting phone up WHILE talking)</li>
                <li>Walking through door and closing it behind you</li>
                <li>Pouring a drink</li>
                <li>Standing to sitting (sit down like you're about to drop some juicy tea)</li>
                <li>Put on makeup/lip gloss</li>
                <li>Background hook (something happening behind you that draws viewers in to make them comment)</li>
              </ul>
            </div>

            {/* Text Hooks */}
            <div className="bg-[#5E150F]/70 p-4 rounded-md mb-6">
              <h3 className="font-bold text-lg mb-3 text-[#FFD700]">Text Hooks</h3>
              <p className="text-[#F7E8CC]/90 italic mb-3">
                Words across the screen that capture viewers' attention
              </p>
              <p className="text-[#F7E8CC]/90 font-medium mb-2">Examples:</p>
              <ul className="list-disc pl-5 space-y-1 text-[#F7E8CC]/90">
                <li>Ask a question that piques curiosity <br/><span className="italic text-sm">"Does your husband do this?"</span></li>
                <li>Negative hooks <br/><span className="italic text-sm">"my husband lied to me"</span></li>
              </ul>
            </div>

            {/* Text Hooks That Worked */}
            <div className="bg-[#5E150F]/70 p-4 rounded-md mb-6">
              <h3 className="font-bold text-lg mb-3 text-[#FFD700]">Text Hooks That Have Worked</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="p-2 bg-[#5E150F] rounded-md">
                  <ul className="list-disc pl-5 space-y-1 text-[#F7E8CC]/90">
                    <li>"You've been warned⚠️"</li>
                    <li>"Am I wrong?"</li>
                    <li>"Am I being gaslit?"</li>
                    <li>"Am I wrong for withholding?"</li>
                    <li>"My husband CAN'T KEEP IT IN HIS PANTS"</li>
                    <li>"My husband has been SNEAKING AROUND"</li>
                    <li>"My man CAN'T KEEP IT IN HIS PANTS"</li>
                    <li>"These cubes will turn you into a FREAKK"</li>
                  </ul>
                </div>
                <div className="p-2 bg-[#5E150F] rounded-md">
                  <ul className="list-disc pl-5 space-y-1 text-[#F7E8CC]/90">
                    <li>"Women "arrive" slower than men"</li>
                    <li>"How I started initiating in my marriage again"</li>
                    <li>"My husband almost got me FIRED"</li>
                    <li>"You will need PRIVACY⚠️"</li>
                    <li>"Who is in the wrong here?"</li>
                    <li>"Should I apologize?"</li>
                    <li>"Should I be mad?"</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Hooks to Try */}
            <div className="bg-[#5E150F]/70 p-4 rounded-md">
              <h3 className="font-bold text-lg mb-3 text-[#FFD700]">Hooks to Try</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="p-2 bg-[#5E150F] rounded-md">
                  <ul className="list-disc pl-5 space-y-1 text-[#F7E8CC]/90">
                    <li>"STOP BEING BORING in bed"</li>
                    <li>"How I saved my marriage from divorce"</li>
                    <li>"This candy could lead to 🫄"</li>
                    <li>"This is your sign to start a FIGHT if your man didn't get you these yet"</li>
                    <li>"Date night TIP ⚠️"</li>
                  </ul>
                </div>
                <div className="p-2 bg-[#5E150F] rounded-md">
                  <ul className="list-disc pl-5 space-y-1 text-[#F7E8CC]/90">
                    <li>"Spooning leads to forking"</li>
                    <li>"Stuck in the roommate phase"</li>
                    <li>"Spicy hinge date"</li>
                    <li>"They get you in the MOOOOD"</li>
                    <li>"They turn you 💡"</li>
                  </ul>
                </div>
              </div>
              <p className="mt-3 text-[#F7E8CC]/90 italic">
                Use these text hooks while telling a relatable story about an experience with spicy cubes. <span className="font-bold">Turn on captions! Make them smaller so they don't get covered.</span>
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-8 bg-[#5E150F] p-5 rounded-md">
            <h3 className="font-bold text-lg mb-3 text-[#FFD700]">CTA (Call to Action) - Video/Comment Replies</h3>
            <p className="text-[#F7E8CC]/90 mb-3">
              Respond to comments in the comment section. This pushes engagement. If someone asks where to get it, reply in the comment section or if the video goes viral, make a video reply with a strong CTA.
            </p>
            
            <div className="bg-[#5E150F]/70 p-4 rounded-md mb-4">
              <h4 className="font-bold text-[#FFD700] mb-2">Verbal CTAs:</h4>
              <ul className="list-disc pl-5 space-y-1 text-[#f7e8cc]/90">
                <li>"They hit you in about 30 mins and last around 3 hours for us....."</li>
                <li>"Date night gummies for the bedroom"</li>
                <li>"I really didn't think these would do anything for women..the others only worked on my husband but these work for both of us."</li>
                <li>"They kick in for us in about 30 mins and last around 3 hours"</li>
                <li>"The best part about these is they actually work on women too..."</li>
                <li>"They last about 1 - 3 hours..."</li>
                <li>"My husband & I take them before every date night.."</li>
                <li>"You just don't understand..."</li>
                <li>"you will feel the effects in about 30 mins"</li>
              </ul>
              <p className="mt-3 text-[#F7E8CC]/90">
                Start your CTA with these hooks then continue to tell them where to "grab" them.
              </p>
            </div>
            
            <p className="text-[#FFD700] font-bold text-center">
              Video replies do VERY well on TikTok but for Instagram it is best to reply to a comment then pin it!
            </p>
          </div>

          {/* Content Ideas */}
          <div className="mt-8 bg-[#5E150F]/30 p-5 rounded-md border border-[#FFD700]/30">
            <h3 className="font-bold text-lg mb-3 text-[#FFD700]">🔥 Content Ideas 🔥</h3>
            <p className="mb-3 text-[#F7E8CC]/90">Here are some proven content ideas that have gone viral and converted viewers into customers:</p>
            
            <div className="space-y-6">
              {/* Example 1 */}
              <div className="bg-[#5E150F]/70 p-4 rounded-md">
                <h4 className="font-bold text-[#FFD700] mb-2">"Am I wrong for withholding?"</h4>
                <p className="mb-2">
                  <a 
                    href="https://www.tiktok.com/@wantspicycubes/video/7480588896976325918?_t=ZT-8usnZIUA7lW&_r=1" 
                    className="text-[#FF851B] hover:underline" 
                    target="_blank" 
                    rel="noopener noreferrer">
                    Watch Example →
                  </a>
                </p>
                <p className="text-sm font-medium mb-2 text-[#F7E8CC]/90">Why this video converted so well:</p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-[#F7E8CC]/90">
                  <li>The <span className="font-bold text-[#FFD700]">hook</span> makes people want to stick around and know why she is "withholding".</li>
                  <li><span className="font-bold text-[#FFD700]">TONE:</span> she is upset and this feels organic</li>
                  <li><span className="font-bold text-[#FFD700]">Controversy:</span> People argued in the comments about if she is wrong for withholding.</li>
                  <li><span className="font-bold text-[#FFD700]">Engagement:</span> she asked a question so people answered. Asking questions in the beginning and end is a sure way to get comments.</li>
                </ul>
              </div>
              
              {/* Example 2 */}
              <div className="bg-[#5E150F]/70 p-4 rounded-md">
                <h4 className="font-bold text-[#FFD700] mb-2">"You've been warned ⚠️"</h4>
                <p className="mb-2">
                  <a 
                    href="https://www.tiktok.com/@wantspicycubes/video/7394602679563029791?_r=1&_t=8rwL66OFxvS" 
                    className="text-[#FF851B] hover:underline" 
                    target="_blank" 
                    rel="noopener noreferrer">
                    Watch Example →
                  </a>
                </p>
                <p className="text-sm font-medium mb-2 text-[#F7E8CC]/90">Why this video converted so well:</p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-[#F7E8CC]/90">
                  <li>The <span className="font-bold text-[#FFD700]">hook</span> about being fired makes the viewer want to know more. <em>How did her HUSBAND almost get her fired?</em></li>
                  <li><span className="font-bold text-[#FFD700]">TONE:</span> The authentic way she tells the story like she's on the facetime with a best friend</li>
                  <li><span className="font-bold text-[#FFD700]">Controversy:</span> a lot of comments were assuming her husband did this on purpose (engagement) which gave an opportunity for her to video reply to the controversy & "clear the air" driving more engagement.</li>
                  <li><span className="font-bold text-[#FFD700]">Retention:</span> The way she told the story kept viewers wanting more. She doesn't tell you what exactly happened or what a spicy cube is until 1 minute into the video.</li>
                </ul>
              </div>
              
              {/* Example 3 */}
              <div className="bg-[#5E150F]/70 p-4 rounded-md">
                <h4 className="font-bold text-[#FFD700] mb-2">"My man can't keep it in his PANTS"</h4>
                <p className="mb-2">
                  <a 
                    href="https://www.tiktok.com/@wantspicycubes/video/7390564568545692958?_r=1&_t=8rwLNJ6qhih" 
                    className="text-[#FF851B] hover:underline" 
                    target="_blank" 
                    rel="noopener noreferrer">
                    Watch Example →
                  </a>
                </p>
                <p className="text-sm font-medium mb-2 text-[#F7E8CC]/90">Why this video converted so well:</p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-[#F7E8CC]/90">
                  <li>The <span className="font-bold text-[#FFD700]">hook</span> made the viewers want to know why her man couldn't keep it in his pants which stopped the scroll making the viewer want more info. <em>Is he cheating? Why cant he keep what in his pants?</em> Leaving the viewer wanting more drives retention which tiktok rewards.</li>
                  <li><span className="font-bold text-[#FFD700]">TONE:</span> The way she speaks to the camera like she's on facetime with a friend seems authentic making the story feel real.</li>
                  <li><span className="font-bold text-[#FFD700]">ACTING:</span> She pretends like her husband is actually there but you can't see him & that he is demanding her to hurry inside because he's FEELING the effects. This was controversial to some and drove a lot of comments (engagement)</li>
                </ul>
              </div>
              
              {/* Example 4 */}
              <div className="bg-[#5E150F]/70 p-4 rounded-md">
                <h4 className="font-bold text-[#FFD700] mb-2">"I can tell you this now because I got fired"</h4>
                <p className="mb-2">
                  <a 
                    href="https://www.tiktok.com/@wantspicycubes/video/7439040002048331054?_t=8rwMOu3MwmM&_r=1" 
                    className="text-[#FF851B] hover:underline" 
                    target="_blank" 
                    rel="noopener noreferrer">
                    Watch Example →
                  </a>
                </p>
                <p className="text-sm mb-2 text-[#F7E8CC]/90">"I'm probably gonna get fired for this"</p>
              </div>
            </div>
          </div>

          {/* Guidelines */}
          <div className="mt-8 bg-[#5E150F] p-5 rounded-md">
            <h3 className="font-bold text-lg mb-3 text-[#FFD700]">Guidelines</h3>
            
            <div className="space-y-4">
              <div className="bg-[#5E150F]/70 p-4 rounded-md">
                <h4 className="font-bold text-[#FFD700] mb-2">TONE</h4>
                <p className="text-[#F7E8CC]/90">
                  When telling a spicy story you want to speak in a playful way as if you're speaking to your best friend on facetime. You want the content to seem relatable & real. It should not sound like a sales pitch.
                </p>
              </div>
              
              <div className="bg-[#5E150F]/70 p-4 rounded-md">
                <h4 className="font-bold text-[#FFD700] mb-2">Restricted Words</h4>
                <p className="text-[#F7E8CC]/90">
                  Do not say sex, orgasm, horny or any curse words that will get flagged.
                </p>
              </div>
              
              <div className="bg-[#5E150F]/70 p-4 rounded-md">
                <h4 className="font-bold text-[#FFD700] mb-2">Alternative Words</h4>
                <p className="text-[#F7E8CC]/90">
                  Spicy, seggs, smeggs (any other words you feel won't be flagged)
                </p>
              </div>
              
              <div className="bg-[#5E150F]/70 p-4 rounded-md">
                <h4 className="font-bold text-[#FFD700] mb-2">Violation of Community Guidelines</h4>
                <p className="text-[#F7E8CC]/90">
                  If you receive a violation for a video, appeal it and please let us know via discord so we can work together to understand why and prevent future violations.
                </p>
              </div>
              
              <div className="bg-[#5E150F]/70 p-4 rounded-md border-2 border-[#FF4136]/50">
                <p className="text-[#F7E8CC] font-bold">
                  DO not incorporate any inappropriate content involving children / date rape
                </p>
                <p className="text-[#F7E8CC]/90 mt-2">
                  For prank content make sure it is noted that you received consent.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-[#F7E8CC]/80 text-center">
            Questions? Contact{' '}
            <a 
              href="mailto:ashli@spicycubes.co" 
              className="font-bold text-[#F7E8CC] hover:text-[#F7E8CC] whitespace-nowrap underline decoration-[2px] hover:decoration-[2.5px] transition-all duration-200 [text-decoration-skip-ink:none]"
            >
              ashli@spicycubes.co
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export const ContentBrief = ContentBriefComponent; 